import { useNavigate } from 'react-router-dom'
import { t } from '@lingui/macro'
import { Theme, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid'

import PageContainer from '@/components/ui/containers/PageContainer'
import { Patient } from '@/core/models/patient.model'
import { useCheckIfExists } from '@/hooks/useCheckIfExists'
import { useDialogState } from '@/hooks/useDialogState'
import { useLanguage } from '@/hooks/useLanguage'
import { createPatient, selectNewlyCreatedPatientId, selectPatientCreateRequestTracker, selectPatients } from '@/redux/reducers/patient.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { sanitizeSecu } from '@/utils/helpers'
import { DMPConnectVitaleCardDialog, useVitaleCardDisplayLogic } from '@/widgets/vitale-card'

import { usePatientDirectorySearch } from '../model/usePatientDirectorySearch'
import { AttachPatientDialog } from './AttachPatientDialog'
import { PatientDirectoryResults } from './PatientDirectoryResults'
import { PatientDirectorySearchControls } from './PatientDirectorySearchControls'
import { PatientsPageActions } from './PatientsPageActions'

const languagesWithSearchByVitale = ['fr']

export function PatientsPage() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const language = useLanguage()
  const patients = useAppSelector(selectPatients)
  const {
    isInsMode,
    insErrors,
    insFilters,
    insSearchError,
    updateInsFilter,
    clearInsFilters,
    handleLegacySearchChange,
    legacySearchValue,
    insPatients,
    legacyPatients,
    totalInsPatients,
    totalLegacyPatients,
    isFetchingInsPatients,
    isFetchingLegacyPatients,
    rowsPerPageOptions,
    page,
    changePage,
    changeItemsPerPage,
    applySelectedIdentity,
  } = usePatientDirectorySearch()
  const { dialogState, setDialogState, closeDialog } = useDialogState<'attach-patient' | 'search-by-vitale-card'>('none')
  const { showDMPConnectVitaleCard } = useVitaleCardDisplayLogic()

  const checkLocal = useCheckIfExists<Patient>({
    successMsg: t`notification.patient.create.success`,
    errorMsg: t`notification.patient.create.error`,
    itemTracker: selectPatientCreateRequestTracker,
    createAction: (patient: Patient) =>
      createPatient({
        netsoinsId: patient.netsoinsId,
        pluginId: patient.netsoinsId,
        userInfo: patient.userInfo,
        secu: sanitizeSecu(patient.secu),
        agreement: `from plugin original id: ${patient._id}`,
      }),
    latestIDSelector: useAppSelector(selectNewlyCreatedPatientId),
    itemList: patients,
    callback: (patient: Patient) => patient && navigate(`/patients/${patient._id}`),
  })

  const canSearchByVitaleCard = !isMobile && languagesWithSearchByVitale.includes(language) && showDMPConnectVitaleCard

  return (
    <PageContainer>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PatientsPageActions
            canSearchByVitaleCard={canSearchByVitaleCard}
            onAttachPatient={() => setDialogState('attach-patient')}
            onSearchByVitaleCard={() => setDialogState('search-by-vitale-card')}
          />
        </Grid>

        <PatientDirectorySearchControls
          isInsMode={isInsMode}
          insErrors={insErrors}
          insFilters={insFilters}
          insSearchError={insSearchError}
          updateInsFilter={updateInsFilter}
          clearInsFilters={clearInsFilters}
          handleLegacySearchChange={handleLegacySearchChange}
          legacySearchValue={legacySearchValue}
        />

        <Grid item xs={12}>
          <PatientDirectoryResults
            isInsMode={isInsMode}
            isMobile={isMobile}
            insPatients={insPatients}
            legacyPatients={legacyPatients}
            totalInsPatients={totalInsPatients}
            totalLegacyPatients={totalLegacyPatients}
            isFetchingInsPatients={isFetchingInsPatients}
            isFetchingLegacyPatients={isFetchingLegacyPatients}
            rowsPerPageOptions={rowsPerPageOptions}
            page={page}
            changePage={changePage}
            changeItemsPerPage={changeItemsPerPage}
            onOpen={(patientId) => {
              if (isInsMode) {
                navigate(`/patients/${patientId}`)
                return
              }
              checkLocal(patientId)
            }}
          />
        </Grid>
      </Grid>

      <AttachPatientDialog open={dialogState === 'attach-patient'} onClose={closeDialog} />
      <DMPConnectVitaleCardDialog
        hideGetIns
        open={dialogState === 'search-by-vitale-card'}
        onClose={closeDialog}
        title={t`Search by vitale card`}
        onSelectPatient={(patient) => {
          applySelectedIdentity(patient.s_name, patient.s_given)
          closeDialog()
        }}
      />
    </PageContainer>
  )
}

export default PatientsPage
